/** Soat sozlamasi (panel ichida): zonalar (qidirish, nomlash, sudrab tartiblash), soniya mili, format, qo'ng'iroq vaqti */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Me } from "@/features/auth/types";
import { Input, Select } from "@/components/ui/field";
import { Toggle } from "@/components/ui/toggle";
import type { WidgetItem } from "../types";
import { useNow } from "@/lib/useNow";
import { LABEL_MAX, MAX_ZONES, type ClockConfig, type ClockZone } from "./config";
import { cityName } from "./names";
import { autoHour12, clockText, searchZones, US_ZONES, zoneParts, zoneStates } from "./time";

type Props = { item: WidgetItem<ClockConfig>; me: Me; onChange: (config: ClockConfig) => void };

const HOURS = Array.from({ length: 25 }, (_, h) => h);
const hh = (h: number) => `${String(h % 24).padStart(2, "0")}:00`;

export function ClockSettings({ item, onChange }: Props) {
  const { t, i18n } = useTranslation("widgets");
  const c = item.config;
  const set = (patch: Partial<ClockConfig>) => onChange({ ...c, ...patch });
  const setZones = (zones: ClockZone[]) => set({ zones });
  // Sudrash uchun barqaror kalit: bir zona ikki marta qo'shilishi mumkin (har xil nom bilan)
  const ids = c.zones.map((z, i) => `${i}:${z.tz}`);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  function onDragEnd(e: DragEndEvent) {
    const from = ids.indexOf(String(e.active.id));
    const to = e.over ? ids.indexOf(String(e.over.id)) : -1;
    if (from < 0 || to < 0 || from === to) return;
    const zones = [...c.zones];
    const [moved] = zones.splice(from, 1);
    zones.splice(to, 0, moved);
    setZones(zones);
  }

  const missingUs = US_ZONES.filter((tz) => !c.zones.some((z) => z.tz === tz));
  const auto = autoHour12(i18n.resolvedLanguage ?? i18n.language);

  return (
    <div className="space-y-4">
      <section>
        <div className="mb-1.5 text-[12px] font-semibold">{t("clock.zones")}</div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <ul className="space-y-1">
              {c.zones.map((z, i) => (
                <ZoneRow
                  key={ids[i]}
                  id={ids[i]}
                  zone={z}
                  canRemove={c.zones.length > 1}
                  onLabel={(label) => setZones(c.zones.map((x, j) => (j === i ? { ...x, label } : x)))}
                  onRemove={() => setZones(c.zones.filter((_, j) => j !== i))}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        {c.zones.length < MAX_ZONES ? (
          <AddZone
            onAdd={(tz) => setZones([...c.zones, { tz, label: "" }])}
            onAddUs={missingUs.length ? () => setZones([...c.zones, ...missingUs.map((tz) => ({ tz, label: "" }))].slice(0, MAX_ZONES)) : undefined}
          />
        ) : (
          <p className="mt-2 text-[11px] text-muted">{t("clock.maxZones", { n: MAX_ZONES })}</p>
        )}
      </section>

      <section className="space-y-3 border-t border-line pt-3">
        <Toggle label={t("clock.seconds")} checked={c.seconds} onChange={(seconds) => set({ seconds })} />
        <label className="flex items-center justify-between gap-3 text-[13px]">
          {t("clock.format")}
          <Select className="h-8 w-[160px]" value={c.hour12 === null ? "auto" : c.hour12 ? "12" : "24"} onChange={(e) => set({ hour12: e.target.value === "auto" ? null : e.target.value === "12" })}>
            <option value="auto">{t("clock.formatAuto", { value: auto ? "12" : "24" })}</option>
            <option value="24">{t("clock.format24")}</option>
            <option value="12">{t("clock.format12")}</option>
          </Select>
        </label>
        <div>
          <div className="flex items-center justify-between gap-3 text-[13px]">
            {t("clock.callHours")}
            <span className="flex items-center gap-1">
              <Select className="h-8 w-[92px]" value={c.call[0]} aria-label={t("clock.callFrom")} onChange={(e) => set({ call: [Number(e.target.value), c.call[1]] })}>
                {HOURS.slice(0, 24).filter((h) => h < c.call[1]).map((h) => (
                  <option key={h} value={h}>{hh(h)}</option>
                ))}
              </Select>
              –
              <Select className="h-8 w-[92px]" value={c.call[1]} aria-label={t("clock.callTo")} onChange={(e) => set({ call: [c.call[0], Number(e.target.value)] })}>
                {HOURS.slice(1).filter((h) => h > c.call[0]).map((h) => (
                  <option key={h} value={h}>{hh(h)}</option>
                ))}
              </Select>
            </span>
          </div>
          <p className="mt-1 text-[11px] text-muted">{t("clock.callHint")}</p>
        </div>
      </section>
    </div>
  );
}

function ZoneRow({ id, zone, canRemove, onLabel, onRemove }: { id: string; zone: ClockZone; canRemove: boolean; onLabel: (label: string) => void; onRemove: () => void }) {
  const { t } = useTranslation("widgets");
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const city = cityName(zone.tz, t);
  // Nom yozilayotganda har harfda saqlanmaydi: chiqqanda yoki Enter'da
  const commit = (value: string) => {
    const label = value.trim().slice(0, LABEL_MAX);
    if (label !== zone.label) onLabel(label);
  };
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn("flex items-center gap-1.5 rounded-2xl border border-line bg-surface py-1 pr-1 pl-0.5", isDragging && "relative z-10 shadow-menu")}
    >
      <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} className="cursor-grab touch-none rounded p-1 text-muted hover:text-ink active:cursor-grabbing" aria-label={t("clock.reorder")}>
        <GripVertical size={14} strokeWidth={1.75} />
      </button>
      <div className="min-w-0 flex-1">
        <input
          defaultValue={zone.label}
          placeholder={city}
          maxLength={LABEL_MAX}
          aria-label={t("clock.label", { city })}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="w-full rounded bg-transparent px-1 text-[13px] font-medium outline-none placeholder:text-ink focus:bg-fill/40"
        />
        <ZoneCaption tz={zone.tz} className="px-1" />
      </div>
      {canRemove && (
        <button type="button" onClick={onRemove} className="rounded p-1 text-muted hover:bg-danger-soft hover:text-danger" aria-label={t("clock.removeZone", { city: zone.label || city })}>
          <X size={14} strokeWidth={1.75} />
        </button>
      )}
    </li>
  );
}

/** Zona ostidagi izoh: hozirgi vaqt va AQSh shtatlari (yoki zona nomi) */
function ZoneCaption({ tz, className }: { tz: string; className?: string }) {
  const { i18n } = useTranslation();
  const now = useNow("minute");
  const { time, suffix } = clockText(zoneParts(now, tz), autoHour12(i18n.resolvedLanguage ?? i18n.language));
  const states = zoneStates(tz);
  const where = states.length ? (states.length > 4 ? `${states.slice(0, 4).join(", ")}…` : states.join(", ")) : tz;
  return (
    <div className={cn("truncate text-[11px] text-muted", className)}>
      <span className="tabular-nums">
        {time}
        {suffix && ` ${suffix}`}
      </span>
      {" · "}
      {where}
    </div>
  );
}

function AddZone({ onAdd, onAddUs }: { onAdd: (tz: string) => void; onAddUs?: () => void }) {
  const { t } = useTranslation("widgets");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const results = useMemo(() => (open ? searchZones(query, (tz) => cityName(tz, t)) : []), [open, query, t]);

  function add(tz: string) {
    onAdd(tz);
    setQuery("");
    setOpen(false);
  }

  return (
    <div className="mt-2">
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <Search size={13} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-muted" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && results[0]) add(results[0]);
              if (e.key === "Escape" && open) {
                e.stopPropagation();
                setOpen(false);
              }
            }}
            placeholder={t("clock.search")}
            aria-label={t("clock.search")}
            className="h-8 pl-7 text-[13px]"
          />
        </div>
        {onAddUs && (
          <button type="button" onClick={onAddUs} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-[10px] border border-border bg-surface px-2 text-[12px] font-medium hover:bg-fill/60">
            <Plus size={13} /> {t("clock.addUs")}
          </button>
        )}
      </div>
      {open && (
        <ul className="mt-1 max-h-[208px] overflow-y-auto rounded-2xl border border-line bg-surface p-1" role="listbox" aria-label={t("clock.search")}>
          {results.length === 0 && <li className="px-2 py-1.5 text-[12px] text-muted">{t("clock.noResults")}</li>}
          {results.map((tz) => (
            <li key={tz}>
              <button type="button" role="option" aria-selected={false} onClick={() => add(tz)} className="w-full rounded px-2 py-1 text-left hover:bg-fill">
                <div className="text-[13px] font-medium">{cityName(tz, t)}</div>
                <ZoneCaption tz={tz} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
